/**
 * GEO 改造的日期序列化纯函数层。
 *
 * 三个消费方共用这一份实现，保证「生成口径」与「巡检口径」不漂移：
 * - 运行时 `src/utils/seo.ts` → `Article` JSON-LD 的 `datePublished` / `dateModified`
 *   与 `Organization` JSON-LD 的 `foundingDate`（需求 3.5、4.5）
 * - 构建期 `scripts/prerender/sitemap.ts` → `<lastmod>`（需求 8.3）
 * - 巡检器 `scripts/geo-inspect/checks.ts` → 校验产物里的时间字符串
 *
 * 全部为纯函数：不读 DOM、不发网络请求、不读全局状态、不读当前时间，
 * 同一输入在 Node 与浏览器中恒返回同一结果。
 *
 * 时区约定：站点全部时间以 CST（UTC+8）表达，输出偏移量固定为 `+08:00`。
 * 解析不依赖宿主机时区，也不依赖 `Date.parse()` 对非标准格式的实现差异
 * （那是 Node 与浏览器结果不一致的主要来源），全部由显式正则处理。
 *
 * 对应需求：3.5、4.5
 */

/** 允许的时间源值类型：ISO / MySQL 文本、epoch 毫秒、`Date`，以及缺失值 */
export type DateInput = string | number | Date | null | undefined;

/** CST 相对 UTC 的偏移分钟数 */
const CST_OFFSET_MINUTES = 8 * 60;

const MS_PER_MINUTE = 60_000;

/** CST 偏移的毫秒数 */
const CST_OFFSET_MS = CST_OFFSET_MINUTES * MS_PER_MINUTE;

/**
 * `toIso8601Cst()` 输出格式的判定正则。
 *
 * 与需求 4.5 的 `YYYY-MM-DDThh:mm:ss+08:00` 逐字符对应，
 * 供属性测试与巡检器复用，避免格式约束在多处各写一遍。
 */
export const ISO8601_CST_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\+08:00$/;

/**
 * 源值与兜底值都无法解析时的输出。
 *
 * 取 CST 纪元起点：它仍然满足 `ISO8601_CST_PATTERN`，
 * 因此「输出恒匹配格式」这条不变量不会因为脏数据被打破；
 * 同时它足够早，不会被误当成一次真实的内容更新。
 */
export const ISO8601_CST_FALLBACK = "1970-01-01T00:00:00+08:00";

/** `ISO8601_CST_FALLBACK` 对应的 epoch 毫秒（1970-01-01T00:00:00+08:00 = -28800000） */
const FALLBACK_EPOCH_MS = -CST_OFFSET_MS;

/** 可格式化的 CST 年份区间：超出则视为脏数据，`pad4` 之外的年份会破坏输出格式 */
const MIN_YEAR = 1;
const MAX_YEAR = 9999;

/** 仅含日期的源值：`2026-02-18` / `2026/2/18` */
const DATE_ONLY_RE = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/;

/**
 * 含时间的源值：`2026-02-18T10:30:00`、`2026-02-18 10:30:00`、
 * `2026-02-18T10:30:00.123Z`、`2026-02-18T10:30:00+08:00`、`2026-02-18T10:30+0800`。
 * 秒、毫秒与时区偏移均可省略；省略时区表示该时间已是 CST 挂钟时间（MySQL 常见形态）。
 */
const DATE_TIME_RE =
  /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})[T ](\d{1,2}):(\d{1,2})(?::(\d{1,2})(?:\.(\d{1,9}))?)?\s*(Z|[+-]\d{2}:?\d{2})?$/i;

/** `founded` 仅含年份：`2015` / `2015年` */
const FOUNDING_YEAR_RE = /^(\d{4})\s*年?$/;

/** `founded` 含年月：`2015-03` / `2015.3` / `2015年3月` */
const FOUNDING_YEAR_MONTH_RE = /^(\d{4})(?:[-/.](\d{1,2})|\s*年\s*(\d{1,2})\s*月)$/;

/** `founded` 含年月日：`2015-03-18` / `2015.3.18` / `2015年3月18日`（可带时间部分） */
const FOUNDING_FULL_DATE_RE =
  /^(\d{4})(?:[-/.](\d{1,2})[-/.](\d{1,2})|\s*年\s*(\d{1,2})\s*月\s*(\d{1,2})\s*日?)(?:[T\s].*)?$/;

function pad(value: number, width: number): string {
  return String(value).padStart(width, "0");
}

const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31] as const;

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

/**
 * 该年该月的天数，用于拒绝 `2026-02-30` 这类会被 `Date.UTC` 静默进位的日期。
 * 自己算而不用 `Date.UTC(year, month, 0)`：后者会把 0–99 的年份映射到 1900–1999。
 */
function daysInMonth(year: number, month: number): number {
  if (month === 2 && isLeapYear(year)) return 29;
  return DAYS_IN_MONTH[month - 1];
}

function isValidYmd(year: number, month: number, day: number): boolean {
  if (!Number.isInteger(year) || year < MIN_YEAR || year > MAX_YEAR) return false;
  if (!Number.isInteger(month) || month < 1 || month > 12) return false;
  return Number.isInteger(day) && day >= 1 && day <= daysInMonth(year, month);
}

/** 把 `Z` / `+08:00` / `-0530` 解析为相对 UTC 的偏移分钟数 */
function parseZoneOffsetMinutes(zone: string): number | null {
  if (zone.toUpperCase() === "Z") return 0;
  const matched = /^([+-])(\d{2}):?(\d{2})$/.exec(zone);
  if (!matched) return null;
  const hours = Number(matched[2]);
  const minutes = Number(matched[3]);
  if (hours > 23 || minutes > 59) return null;
  return (matched[1] === "-" ? -1 : 1) * (hours * 60 + minutes);
}

/** epoch 毫秒是否落在可格式化的 CST 年份区间内 */
function isFormattableEpoch(epochMs: number): boolean {
  if (!Number.isFinite(epochMs)) return false;
  const year = new Date(epochMs + CST_OFFSET_MS).getUTCFullYear();
  return year >= MIN_YEAR && year <= MAX_YEAR;
}

/**
 * 把源值解析为 epoch 毫秒；无法解析或超出可格式化区间时返回 `null`。
 *
 * 解析规则：
 * - `Date` 实例取 `getTime()`，`Invalid Date` 视为无法解析
 * - 有限数值视为 epoch 毫秒
 * - 字符串按 `DATE_ONLY_RE` / `DATE_TIME_RE` 匹配；**不含时区偏移的文本按 CST 挂钟时间理解**，
 *   仅含日期的源值补 `00:00:00`（需求 4.5）
 */
function parseEpochMs(value: DateInput): number | null {
  if (value === null || value === undefined) return null;

  if (value instanceof Date) {
    const time = value.getTime();
    return isFormattableEpoch(time) ? time : null;
  }

  if (typeof value === "number") {
    return isFormattableEpoch(value) ? value : null;
  }

  const text = value.trim();
  if (text === "") return null;

  const dateOnly = DATE_ONLY_RE.exec(text);
  if (dateOnly) {
    return fromParts(
      Number(dateOnly[1]),
      Number(dateOnly[2]),
      Number(dateOnly[3]),
      0,
      0,
      0,
      0,
      CST_OFFSET_MINUTES,
    );
  }

  const dateTime = DATE_TIME_RE.exec(text);
  if (!dateTime) return null;

  const zone = dateTime[8];
  const offsetMinutes = zone === undefined ? CST_OFFSET_MINUTES : parseZoneOffsetMinutes(zone);
  if (offsetMinutes === null) return null;

  const fraction = dateTime[7];

  return fromParts(
    Number(dateTime[1]),
    Number(dateTime[2]),
    Number(dateTime[3]),
    Number(dateTime[4]),
    Number(dateTime[5]),
    dateTime[6] === undefined ? 0 : Number(dateTime[6]),
    fraction === undefined ? 0 : Number(fraction.slice(0, 3).padEnd(3, "0")),
    offsetMinutes,
  );
}

/** 由日历字段组装 epoch 毫秒，字段越界（如 2 月 30 日、25 点）时返回 `null` */
function fromParts(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
  millisecond: number,
  offsetMinutes: number,
): number | null {
  if (!isValidYmd(year, month, day)) return null;
  if (hour > 23 || minute > 59 || second > 59) return null;

  // 用 setUTCFullYear 而不是 Date.UTC(year, ...)：后者把 0–99 的年份映射到 1900–1999
  const utc = new Date(0);
  utc.setUTCFullYear(year, month - 1, day);
  utc.setUTCHours(hour, minute, second, millisecond);

  const epochMs = utc.getTime() - offsetMinutes * MS_PER_MINUTE;

  return isFormattableEpoch(epochMs) ? epochMs : null;
}

/** 把 epoch 毫秒格式化为 `YYYY-MM-DDThh:mm:ss+08:00` */
function formatEpochMs(epochMs: number): string {
  const shifted = new Date(epochMs + CST_OFFSET_MS);

  const date = `${pad(shifted.getUTCFullYear(), 4)}-${pad(shifted.getUTCMonth() + 1, 2)}-${pad(shifted.getUTCDate(), 2)}`;
  const time = `${pad(shifted.getUTCHours(), 2)}:${pad(shifted.getUTCMinutes(), 2)}:${pad(shifted.getUTCSeconds(), 2)}`;

  return `${date}T${time}+08:00`;
}

/**
 * 把任意时间源值序列化为 `YYYY-MM-DDThh:mm:ss+08:00`（需求 4.5、8.3）。
 *
 * - 仅含日期的源值（`2026-02-18`）以 `T00:00:00+08:00` 补齐
 * - 不带时区偏移的文本按 CST 挂钟时间理解，带偏移或 `Z` 的按绝对时刻换算到 CST
 * - 源值无法解析时改用 `fallback`（预渲染场景常传 `manifest.site.generatedAt`），
 *   两者都无法解析时返回 `ISO8601_CST_FALLBACK`
 *
 * 返回值恒匹配 `ISO8601_CST_PATTERN`。
 */
export function toIso8601Cst(value: DateInput, fallback: DateInput = null): string {
  const epochMs = parseEpochMs(value) ?? parseEpochMs(fallback) ?? FALLBACK_EPOCH_MS;
  return formatEpochMs(epochMs);
}

/**
 * 保证 `dateModified` 不早于 `datePublished`（需求 4.5）。
 *
 * `modified` 缺失、无法解析或早于 `published` 时返回 `published` 的序列化结果；
 * `published` 也无法解析时以 `ISO8601_CST_FALLBACK` 作为下界，
 * 使「返回值 >= `toIso8601Cst(published)`」在任何输入下都成立。
 *
 * 两个返回值格式相同、偏移相同，因此字典序比较与时间先后一致，
 * 调用方可以直接对字符串做比较。
 */
export function clampDateModified(published: DateInput, modified: DateInput): string {
  const publishedMs = parseEpochMs(published) ?? FALLBACK_EPOCH_MS;
  const modifiedMs = parseEpochMs(modified);

  if (modifiedMs === null || modifiedMs < publishedMs) {
    return formatEpochMs(publishedMs);
  }

  return formatEpochMs(modifiedMs);
}

/**
 * 把 `biz_about_config.founded` 转成 `Organization` JSON-LD 的 `foundingDate`（需求 3.5）。
 *
 * `founded` 在库中是自由文本，因此这里接受多种写法：
 * - 仅含年份（`2015`、`2015年`，或数值 `2015`）→ 输出 `YYYY`
 * - 含年月（`2015-03`、`2015年3月`）→ 信息不足以给出日，同样输出 `YYYY`
 * - 含年月日（`2015-03-18`、`2015/3/18`、`2015年3月18日`，可带时间部分）→ 输出 `YYYY-MM-DD`
 * - `Date` 实例或 epoch 毫秒数值 → 按 CST 取日期部分，输出 `YYYY-MM-DD`
 *
 * 无法解析（空串、纯空白、`null`、`不详` 等）时返回 `null`，
 * 由调用方 `buildOrganizationLd()` 省略 `foundingDate` 键（需求 3.10）。
 */
export function toFoundingDate(value: DateInput): string | null {
  if (value === null || value === undefined) return null;

  if (value instanceof Date || typeof value === "number") {
    // 四位整数按年份理解：`founded` 最常见的取值就是 2015 这样的年份数字
    if (typeof value === "number" && Number.isInteger(value) && value >= 1000 && value <= MAX_YEAR) {
      return pad(value, 4);
    }
    const epochMs = parseEpochMs(value);
    return epochMs === null ? null : formatEpochMs(epochMs).slice(0, 10);
  }

  const text = value.trim();
  if (text === "") return null;

  const yearOnly = FOUNDING_YEAR_RE.exec(text);
  if (yearOnly) {
    const year = Number(yearOnly[1]);
    return year >= MIN_YEAR ? pad(year, 4) : null;
  }

  const yearMonth = FOUNDING_YEAR_MONTH_RE.exec(text);
  if (yearMonth) {
    const year = Number(yearMonth[1]);
    const month = Number(yearMonth[2] ?? yearMonth[3]);
    return isValidYmd(year, month, 1) ? pad(year, 4) : null;
  }

  const fullDate = FOUNDING_FULL_DATE_RE.exec(text);
  if (fullDate) {
    const year = Number(fullDate[1]);
    const month = Number(fullDate[2] ?? fullDate[4]);
    const day = Number(fullDate[3] ?? fullDate[5]);
    if (!isValidYmd(year, month, day)) return null;
    return `${pad(year, 4)}-${pad(month, 2)}-${pad(day, 2)}`;
  }

  return null;
}
